import React, { useState } from 'react';
import { Campaign } from 'src/types/campaign';

interface FiltersModalProps {
  onClose: () => void;
  onApplyFilters: (filters: {
    status?: string;
    campaignName?: string;
    sortOrder?: 'asc' | 'desc';
    objective?: string;
  }) => void;
  campaigns: Campaign[];
}

const FiltersModal = ({ onClose, onApplyFilters, campaigns }: FiltersModalProps) => {
  // State for filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [campaignNameFilter, setCampaignNameFilter] = useState<string>('');
  const [objectiveFilter, setObjectiveFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Get unique statuses and objectives for dropdowns
  const statusOptions = ['all', ...new Set(campaigns.map(c => c.status))];
  const objectiveOptions = ['all', ...new Set(campaigns.map(c => c.objective))];

  const handleApply = () => {
    const filters: {
      status?: string;
      campaignName?: string;
      sortOrder?: 'asc' | 'desc';
      objective?: string;
    } = {};

    if (statusFilter !== 'all') {
      filters.status = statusFilter;
    }

    if (campaignNameFilter) {
      filters.campaignName = campaignNameFilter;
    }

    if (objectiveFilter !== 'all') {
      filters.objective = objectiveFilter;
    }

    filters.sortOrder = sortOrder;

    onApplyFilters(filters);
    onClose();
  };

  const handleReset = () => {
    setStatusFilter('all');
    setCampaignNameFilter('');
    setObjectiveFilter('all');
    setSortOrder('desc');
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="flex flex-col items-start p-6 gap-5 w-[602px] bg-white rounded-xl">
        {/* Header */}
        <div className="flex justify-between items-center w-full">
          <h2 className="text-2xl font-bold text-gray-900">Filters</h2>
          <button onClick={onClose} className="p-2">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="#000000"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Status Filter */}
        <div className="w-full">
          <label className="text-base font-normal text-gray-800">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="w-full p-3 border border-gray-400 rounded-lg mt-2"
          >
            {statusOptions.map(status => (
              <option key={status} value={status}>
                {status === 'all' ? 'All Statuses' : status}
              </option>
            ))}
          </select>
        </div>

        {/* Campaign Name Filter */}
        <div className="w-full">
          <label className="text-base font-normal text-gray-800">Campaign Name</label>
          <input
            type="text"
            value={campaignNameFilter}
            onChange={e => setCampaignNameFilter(e.target.value)}
            placeholder="Search by campaign name"
            className="w-full p-3 border border-gray-400 rounded-lg mt-2"
          />
        </div>

        {/* Objective Filter */}
        <div className="w-full">
          <label className="text-base font-normal text-gray-800">Objective</label>
          <select
            value={objectiveFilter}
            onChange={e => setObjectiveFilter(e.target.value)}
            className="w-full p-3 border border-gray-400 rounded-lg mt-2"
          >
            {objectiveOptions.map(obj => (
              <option key={obj} value={obj}>
                {obj === 'all' ? 'All Objectives' : obj.split('_').join(' ').toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Sorting Options */}
        <div className="w-full">
          <label className="text-lg font-normal text-gray-800">Sort by Results</label>
          <div className="flex flex-col gap-3 mt-2">
            {/* Descending Order (default) */}
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setSortOrder('desc')}
            >
              <div className="w-6 h-6 border-2 border-blue-500 rounded-full flex items-center justify-center">
                {sortOrder === 'desc' && <div className="w-4 h-4 bg-blue-500 rounded-full"></div>}
              </div>
              <span className="text-lg font-normal text-gray-900">Highest to Lowest</span>
            </div>
            {/* Ascending Order */}
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setSortOrder('asc')}
            >
              <div className="w-6 h-6 border-2 border-gray-200 rounded-full flex items-center justify-center">
                {sortOrder === 'asc' && <div className="w-4 h-4 bg-blue-500 rounded-full"></div>}
              </div>
              <span className="text-lg font-normal text-gray-900">Lowest to Highest</span>
            </div>
          </div>
        </div>

        {/* Reset All Button */}
        <button className="text-sm font-normal text-blue-500 underline" onClick={handleReset}>
          Reset all
        </button>

        {/* Action Buttons */}
        <div className="flex gap-3 w-full mt-4">
          <button
            className="flex-1 p-3 bg-gray-100 rounded-lg text-base font-medium text-gray-900"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="flex-1 p-3 bg-blue-500 rounded-lg text-base font-medium text-white"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FiltersModal;
